import { styled } from 'styled-components';
import * as Ariakit from '@ariakit/react';

import { CheckboxIcon, ChevronIcon } from '../../../assets/icons';
import { Animate } from '../../Animate';
import { SearchSelectProps } from './SearchSelect';
import { OptionType } from './types';
import { Text } from '../../Text';
import { ChevronIconWrapper, StyledSelectHeader } from './Select';
import { ALL_OPTION } from './types/allOption';

export const SelectHeader = ({
  selectStore,
  options,
  label,
  testId,
  disabled,
  hasError,
}: Pick<SearchSelectProps, 'options' | 'label' | 'testId' | 'disabled' | 'hasError'> & {
  selectStore: Ariakit.SelectStore;
}) => {
  const open = Ariakit.useStoreState(selectStore, 'open');

  const renderValue = (value: string | string[]) => {
    if (!value || value.length === 0) {
      return label;
    }
    if (Array.isArray(value)) {
      return getRenderedValueForMultiSelect(value, selectStore, options);
    }
    return getRenderedValueForSingleSelect(value, selectStore, options);
  };

  return (
    <StyledSelectHeader
      store={selectStore}
      role="listbox"
      data-testid={testId}
      type="button"
      disabled={disabled}
      error={hasError ? 'error' : ''}
    >
      <Ariakit.SelectValue store={selectStore}>{renderValue}</Ariakit.SelectValue>
      <ChevronIconWrapper>
        <Animate.Rotate isTriggered={open} degrees={180}>
          <ChevronIcon width={14} variant="thin" direction="downward" color={disabled ? 'black30' : 'black'} />
        </Animate.Rotate>
      </ChevronIconWrapper>
    </StyledSelectHeader>
  );
};

export const SelectItemContent = ({
  option,
  multiple,
  showFavorites,
  hideDescription,
}: {
  option: OptionType;
  multiple: boolean;
  showFavorites?: boolean;
  hideDescription: boolean;
}) => {
  return (
    <>
      {multiple && option.id !== ALL_OPTION && (
        <Ariakit.SelectItemCheck
          render={({ children }) => (
            <CheckboxIcon
              variant={children ? 'checked' : ''} // Ariakit.SelectItemCheck will render if the item is selected. Aka children will be defined.
              color={option.isDisabled ? 'black60' : 'black'}
              width={22}
              marginRight={10}
            />
          )}
        />
      )}
      {/* TODO: Add item.icon TODO: Use IconNames and Assets[iconName] instead? */}
      <TextWrapper title={option.name}>
        <Text color={option.isDisabled ? 'black60' : 'black'} fontWeight={option.bold ? 'semibold' : 'normal'}>
          {option.name}
        </Text>
        {!hideDescription && option.description && <Text color="black70">{option.description}</Text>}
      </TextWrapper>
      {/* TODO: Add showFavorites */}
    </>
  );
};

const getRenderedValueForMultiSelect = (values: string[], selectStore: Ariakit.SelectStore, options: OptionType[]) => {
  const names: string[] = [];
  const valuesNotFoundInSelectStore: string[] = [];

  values.forEach(value => {
    const item = selectStore.item(value) as OptionType | null;
    item ? names.push(item.name) : valuesNotFoundInSelectStore.push(value);
  });
  if (valuesNotFoundInSelectStore.length > 0) {
    const restOfNames = options.filter(({ id }) => valuesNotFoundInSelectStore.includes(id)).map(e => e.name);
    names.concat(restOfNames);
  }

  return names.join(', ');
};

const getRenderedValueForSingleSelect = (value: string, selectStore: Ariakit.SelectStore, options: OptionType[]) => {
  const item = selectStore.item(value) as OptionType | null;
  if (item) {
    return item.name;
  }
  return options.find(({ id }) => id === value)?.name || value;
};

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

export const SelectWrapper = styled.div`
  // The only way to get the element to not shrink/stretch... Please trye to solve this otherwise!
  display: grid;
  width: 100%;
`;
